import { Component, EventEmitter, forwardRef, inject, Input, OnInit, Optional, Output, SkipSelf } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ControlContainer, FormGroupDirective, NG_VALUE_ACCESSOR, NgForm } from '@angular/forms';
import { BaseInput, controlContainerFactory } from '@ui-v2/atoms/inputs/base-input';
import { DropdownInput, UiFormModule } from '@ui/form';
import { AsyncPipe } from '@angular/common';
import { map } from 'rxjs/operators';
import { WaterSandbox } from '@app/water/sandboxes/water.sandbox';
import { SelectInput } from '@ui-v2/atoms/inputs/select-input/select-input.component';
import { WaterSupplier } from '@ui/water/interfaces/water-supplier';
import { DbUtils } from '@core/utils';
import { SelectInputGroup } from '@ui-v2/atoms/inputs/types/select-input.types';

export const waterSupplierOptionsFactory = ({ name, ...rest }: WaterSupplier): DropdownInput<string> => ({
    id: `water-supplier-${name}`,
    name: 'water-suppliers',
    value: DbUtils.getStringId(rest),
    label: name,
});

export const waterSupplierOptionsForMobileFactory = (suppliers: WaterSupplier[]): SelectInputGroup<string> => ({
    name: 'water-suppliers',
    options: suppliers.map((supplier) => ({
        value: DbUtils.getStringId(supplier),
        labelResource: supplier.name,
    })),
});

@Component({
    selector: 'app-water-supplier-input',
    standalone: true,
    imports: [TranslateModule, UiFormModule, AsyncPipe, SelectInput],
    template: `
        @if (forMobile) {
            <div class="relative">
                <ui-v2-select-input
                    [(ngModel)]="innerModel"
                    (ngModelChange)="onModelChange($event)"
                    (blur)="onBlur()"
                    [disabled]="innerDisabled"
                    [legend]="'V2.SERVICES.WATER.SUPPLIER.LEGEND' | translate"
                    [placeholder]="'V2.SERVICES.WATER.SUPPLIER.PLACEHOLDER' | translate"
                    [inputGroup]="waterSupplierOptionsForMobile$ | async"
                ></ui-v2-select-input>
            </div>
        } @else {
            <ui-searchable-dropdown-input
                [options]="waterSupplierOptions$ | async"
                [placeholder]="'V2.SERVICES.WATER.SUPPLIER.PLACEHOLDER' | translate"
                [label]="'V2.SERVICES.WATER.SUPPLIER.LEGEND' | translate"
                [(ngModel)]="innerModel"
                (ngModelChange)="onModelChange($event)"
                (blur)="onBlur()"
                [disabled]="innerDisabled"
                width="100%"
                ngDefaultControl
            ></ui-searchable-dropdown-input>
        }
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => WaterSupplierInputComponent),
            multi: true,
        },
        {
            provide: ControlContainer,
            useFactory: controlContainerFactory,
            deps: [
                [new Optional(), new SkipSelf(), FormGroupDirective],
                [new Optional(), new SkipSelf(), NgForm],
            ],
        },
    ],
})
export class WaterSupplierInputComponent extends BaseInput<string> implements OnInit {
    @Input() public forMobile = false;
    @Output() public supplierChanged = new EventEmitter<string>();

    private readonly waterSandbox = inject(WaterSandbox);

    public readonly waterSupplierOptionsForMobile$ = this.waterSandbox.waterSuppliers$.pipe(
        map((waterSuppliers) => waterSupplierOptionsForMobileFactory(waterSuppliers)),
    );

    public readonly waterSupplierOptions$ = this.waterSandbox.waterSuppliers$.pipe(
        map((waterSuppliers) => waterSuppliers?.map(waterSupplierOptionsFactory)),
    );

    public ngOnInit(): void {
        this.waterSandbox.getWaterSuppliers();
    }

    public writeValue(value: string): void {
        this.innerModel = value ?? null;
    }

    public onModelChange(value: string): void {
        this.propagateChange(value);
        this.supplierChanged.emit(value);
    }
}
